import AddIcon from '@mui/icons-material/AddOutlined';
import { Box, Button, Card, Checkbox, FormControlLabel, IconButton } from '@mui/material';
import { ObjectID } from 'bson';
import { useFindFoldersQuery } from 'client/modules/Surveys/folderApiSlice';
import React, { useMemo } from 'react';
import { IoTrashOutline as DeleteIcon, IoPencil as EditIcon } from 'react-icons/io5';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useOutletContext } from 'react-router';
import useOpenClose from '../../../../../util/useOpenClose';
import styles from '../IntegrationsListPage.css';
import FetchPropertiesModal from './FetchPropertiesModal';
import SyncPropertiesModal from './SyncPropertiesModal';

const messages = defineMessages({
  syncResponses: {
    id: 'IntegrationsSettings.HubspotSyncResponses',
    defaultMessage: 'Sync responses to contacts',
  },
  hubspotPropertyPrefix: {
    id: 'IntegrationsSettings.HubspotPropertyPrefix',
    defaultMessage: 'Property prefix',
  },
  hubspotPropertyShowSurvey: {
    id: 'IntegrationsSettings.HubspotPropertyShowSurvey',
    defaultMessage: 'Hubspot Property ID',
  },
  syncProperties: {
    id: 'IntegrationsSettings.syncProperties',
    defaultMessage: 'Sync Properties',
  },
  addSyncSurvey: {
    id: 'IntegrationsSettings.HubspotAddSyncSurvey',
    defaultMessage: 'Add survey to sync',
  },
  surveysRequired: {
    id: 'IntegrationsSettings.HubspotSurveysRequired',
    defaultMessage: 'Please select some surveys.',
  },
  survey: {
    id: 'IntegrationsSettings.HubspotSurvey',
    defaultMessage: 'Survey',
  },
  properties: {
    id: 'IntegrationsSettings.HubspotProperties',
    defaultMessage: 'Properties',
  },
  all: {
    id: 'IntegrationsSettings.All',
    defaultMessage: 'All',
  },
  active: {
    id: 'activeSurveys',
    defaultMessage: 'Active surveys',
  },
  edit: {
    id: 'IntegrationsSettings.Edit',
    defaultMessage: 'Edit',
  },
  remove: {
    id: 'IntegrationsSettings.Remove',
    defaultMessage: 'Remove',
  },
  noSurvey: {
    id: 'IntegrationsSettings.noSurvey',
    defaultMessage: '[No survey selected]',
  },
  hubspotPropertyLabel: {
    id: 'IntegrationsSettings.HubspotPropertyLabel',
    defaultMessage: 'Hubspot Property Label',
  },
  property: {
    id: 'IntegrationsSettings.Property',
    defaultMessage: 'Property',
  },
  addProperty: {
    id: 'IntegrationsSettings.AddProperty',
    defaultMessage: 'Add Property',
  },
  removeProperty: {
    id: 'IntegrationsSettings.RemoveProperty',
    defaultMessage: 'Remove',
  },
  additionalQuestion: {
    id: 'IntegrationsSettings.AdditionalQuestion',
    defaultMessage: 'Question {n}',
  },
  noSurveys: {
    id: 'IntegrationsSettings.noSurveys',
    defaultMessage: 'No surveys to sync',
  },
  syncResponsesDescription: {
    id: 'IntegrationsSettings.syncResponsesDescription',
    defaultMessage:
      'Every time a response is created, we will update some properties of the contact in Hubspot matching the response email.',
  },
  FetchPropertiesEnabled: {
    id: 'IntegrationsSettings.FetchPropertiesEnabled',
    defaultMessage: 'Fetch properties from Hubspot',
  },
  FetchPropertiesEnabledDescription: {
    id: 'IntegrationsSettings.FetchPropertiesEnabledDescription',
    defaultMessage: 'Fetch and store additional properties from Hubspot contacts.',
  },
  EditFetchProperties: {
    id: 'IntegrationsSettings.EditFetchProperties',
    defaultMessage: 'Edit Properties to Fetch',
  },
  ShowSurveyByProperty: {
    id: 'IntegrationsSettings.ShowSurveyByProperty',
    defaultMessage: 'Show survey by property',
  },
  PropertyShowSurvey: {
    id: 'IntegrationsSettings.PropertyShowSurvey',
    defaultMessage: 'Property to show survey',
  },
  FetchPropertiesSummary: {
    id: 'IntegrationsSettings.FetchPropertiesSummary',
    defaultMessage: 'Properties to fetch from Hubspot',
  },
  FetchPropertiesCount: {
    id: 'IntegrationsSettings.FetchPropertiesCount',
    defaultMessage:
      '{count, plural, =0 {No properties configured} one {# property} other {# properties}}',
  },
});

const defaultProperties = [
  'score',
  'comment',
  'permalink',
  'responded_at',
  'response_updated_at',
  'group',
  'survey_type',
];

const IntegrationConfigHubspot = () => {
  const [isModalOpen, openModal, closeModal, { syncSurveyIdx }] = useOpenClose();
  const [isFetchModalOpen, openFetchModal, closeFetchModal] = useOpenClose();
  const { formatMessage } = useIntl();
  const { integration, onIntegrationsPropertiesChange, surveys, formRef } = useOutletContext();

  const { data: folders, isSuccess } = useFindFoldersQuery({});
  const foldersMap = useMemo(
    () =>
      isSuccess
        ? folders.reduce((acc, curr) => ({ ...acc, [curr._id]: `${curr.name} / ` }), {})
        : {},
    [folders],
  );
  const surveyOptions = useMemo(
    () =>
      surveys
        .filter(s => !s.archived)
        .map(s => ({
          value: s.id,
          folder: foldersMap[s.folder] || '',
          name: s.name || s.brand,
        }))
        .sort((a, b) => a.folder.localeCompare(b.folder) || a.name.localeCompare(b.name)),
    [foldersMap, surveys],
  );
  const { portalId, syncResponses = false, syncSurveys = [] } = integration || {};
  const fetchPropertiesEnabled = integration.fetchPropertiesEnabled ?? true;
  const fetchProperties = integration.fetchProperties || {};

  const onToggle = name => event => {
    onIntegrationsPropertiesChange(event, 'hubspot', { [name]: event.target.checked });
    if (event.target.checked && !syncSurveys?.length) {
      onIntegrationsPropertiesChange(null, 'hubspot', {
        syncSurveys: [
          {
            _id: new ObjectID().toString(),
            survey: surveys[0]?.id,
            propertyPrefix: 'promoterninja',
            syncProperties: defaultProperties.map(prop => ({ name: prop })),
          },
        ],
      });
    }
  };

  const addSyncSurvey = () => {
    const newSyncSurveys = [
      ...syncSurveys,
      {
        _id: new ObjectID().toString(),
        syncProperties: defaultProperties.map(prop => ({ name: prop })),
      },
    ];
    onIntegrationsPropertiesChange(null, 'hubspot', { syncSurveys: newSyncSurveys });
    openModal({ syncSurveyIdx: newSyncSurveys.length - 1 });
  };

  const removeRule = index => () => {
    const newSyncSurveys = syncSurveys.filter((r, idx) => idx !== index);
    onIntegrationsPropertiesChange(null, 'hubspot', { syncSurveys: newSyncSurveys });
  };

  // Handler for saving sync properties from modal
  const handleSyncPropertiesSave = updatedSurvey => {
    const newSyncSurveys = integration.syncSurveys.map((r, i) =>
      i === syncSurveyIdx ? updatedSurvey : r,
    );
    onIntegrationsPropertiesChange(null, 'hubspot', { syncSurveys: newSyncSurveys });
    setTimeout(() => {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, 10);
  };

  // Handler for saving fetch properties from modal
  const handleFetchPropertiesSave = props => {
    onIntegrationsPropertiesChange(null, 'hubspot', { fetchProperties: props });
    setTimeout(() => {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, 10);
  };

  return (
    <Box
      sx={{
        gap: 6,
        display: 'flex',
        flexDirection: 'column',
        mb: 4,
      }}
    >
      <span>
        <FormattedMessage
          id="IntegrationsSettings.HubID"
          defaultMessage="Hub ID: {portalId}"
          values={{
            portalId,
          }}
        />
      </span>
      <Box>
        <Box className={styles.syncSurveysDivider} />
        <FormControlLabel
          control={
            <Checkbox
              checked={syncResponses}
              onChange={onToggle('syncResponses')}
              color="primary"
            />
          }
          label={<Box sx={{ fontWeight: 600 }}>{formatMessage(messages.syncResponses)}</Box>}
          className={styles.syncSurveysCheckboxLabel}
        />
        <div className={styles.syncSurveysCheckboxDescription}>
          {formatMessage(messages.syncResponsesDescription)}
        </div>
        {(!syncResponses && !syncSurveys?.length) || (
          <Box
            className={
              styles.syncSurveysSection +
              (!syncResponses ? ` ${styles.syncSurveysSectionDisabled}` : '')
            }
          >
            <Box className={styles.syncSurveysAddBtn}>
              <Button
                variant="text"
                color="primary"
                startIcon={<AddIcon />}
                onClick={addSyncSurvey}
                className={styles.syncSurveysAddBtnInner}
                disabled={!syncResponses}
              >
                {formatMessage(messages.addSyncSurvey)}
              </Button>
            </Box>
            <Card variant="outlined" className={styles.syncSurveysTable}>
              <Box className={styles.syncSurveysHeader}>
                <Box className={styles.syncSurveysHeaderSurvey}>
                  {formatMessage(messages.survey)}
                </Box>
                <Box className={styles.syncSurveysHeaderProperties}>
                  {formatMessage(messages.properties)}
                </Box>
                <Box className={styles.syncSurveysHeaderActions} />
              </Box>
              {syncSurveys?.length === 0 ? (
                <Box className={styles.syncSurveysEmpty}>{formatMessage(messages.noSurveys)}</Box>
              ) : (
                syncSurveys.map((syncSurvey, index) => {
                  const survey = surveyOptions.find(
                    s => syncSurvey.survey && s.value === syncSurvey.survey,
                  );
                  return (
                    <Box
                      key={syncSurvey._id}
                      className={styles.syncSurveysRow}
                      onClick={
                        syncResponses ? () => openModal({ syncSurveyIdx: index }) : undefined
                      }
                      role="button"
                      tabIndex={0}
                      style={{ outline: 'none' }}
                      onKeyDown={e => {
                        if (syncResponses && (e.key === 'Enter' || e.key === ' '))
                          openModal({ syncSurveyIdx: index });
                      }}
                    >
                      <Box className={styles.syncSurveysRowSurvey}>
                        {survey ? survey.name : formatMessage(messages.noSurvey)}
                      </Box>
                      <Box className={styles.syncSurveysRowProperties}>
                        {syncSurvey.syncProperties?.length || 0}
                      </Box>
                      <Box className={styles.syncSurveysRowActions}>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            if (syncResponses) openModal({ syncSurveyIdx: index });
                          }}
                          aria-label={formatMessage(messages.edit)}
                          disabled={!syncResponses}
                          title={formatMessage(messages.edit)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            if (syncResponses) removeRule(index)();
                          }}
                          aria-label={formatMessage(messages.remove)}
                          disabled={!syncResponses}
                          title={formatMessage(messages.remove)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Card>
          </Box>
        )}
        <Box className={styles.syncSurveysDivider} />

        <FormControlLabel
          control={
            <Checkbox
              checked={fetchPropertiesEnabled}
              onChange={e =>
                onIntegrationsPropertiesChange(e, 'hubspot', {
                  fetchPropertiesEnabled: e.target.checked,
                })
              }
              color="primary"
            />
          }
          label={
            <Box sx={{ fontWeight: 600 }}>{formatMessage(messages.FetchPropertiesEnabled)}</Box>
          }
          className={styles.syncSurveysCheckboxLabel}
        />
        <Box className={styles.syncSurveysCheckboxDescription}>
          {formatMessage(messages.FetchPropertiesEnabledDescription)}
        </Box>
        {fetchPropertiesEnabled && (
          <Box className={styles.fetchPropertiesSection}>
            <Box>
              {formatMessage(messages.FetchPropertiesCount, {
                count: Object.keys(fetchProperties).length,
              })}
            </Box>
            <IconButton
              color="primary"
              size="small"
              onClick={openFetchModal}
              aria-label={formatMessage(messages.edit)}
              title={formatMessage(messages.edit)}
            >
              <EditIcon />
            </IconButton>
          </Box>
        )}
        <Box className={styles.syncSurveysDivider} />
      </Box>
      <SyncPropertiesModal
        open={isModalOpen}
        onClose={closeModal}
        syncSurveyIdx={syncSurveyIdx}
        onSave={handleSyncPropertiesSave}
      />
      <FetchPropertiesModal
        open={isFetchModalOpen}
        onClose={closeFetchModal}
        fetchProperties={fetchProperties}
        onSave={handleFetchPropertiesSave}
      />
    </Box>
  );
};

export default IntegrationConfigHubspot;
